import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import {NavLink} from 'react-router-dom';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';

import {ExpandMore} from '@mui/icons-material';
import {AppLink, AppLinkCategory} from '../../../utils/routes';
import {useStyles} from './drawerStyles';
import {DrawerMenuProps, DrawerMenuRef} from './drawerMenu';
import {isInViewport} from '../../hooks/useOnScreen';

import {useMutationObservable} from '../../hooks/useMutationObservable';
import clsx from 'clsx';

const config = {
  config: {
    attributes: true,
    childList: true,
    subtree: true
  }
};
export const ExpandedMenu = forwardRef<DrawerMenuRef, DrawerMenuProps>((props, ref) => {
  const theme = useTheme();
  const thisRef = useRef<HTMLDivElement>(null);
  const refLinkCategoryIsMatch = useRef<HTMLAnchorElement>(null);
  const [expanded, setExpanded] = useState<AppLinkCategory[]>(
    props.selectedCategory ? [props.selectedCategory] : []
  );

  useImperativeHandle(ref, () => ({setExpanded, refExpandedDrawer: thisRef, refLinkCategoryIsMatch}));

  function changeExpand(appLink: AppLinkCategory) {
    if (expanded.includes(appLink)) {
      setExpanded(expanded.filter((i) => i !== appLink));
    } else {
      setExpanded([...expanded, appLink]);
    }
  }

  useEffect(() => {
    setExpanded(prev => props.selectedCategory ? [...prev, props.selectedCategory] : prev);
  }, [props.selectedCategory]);

  const handleClick = (event: React.MouseEvent, entry: AppLink) => {
    if (!entry.enabled) {
      event.preventDefault();
    }
  };

  const scrollInToView = useCallback(() => {
    if (!props.selectedCategory)
      return;
    const visibleLinkCategory = isInViewport(refLinkCategoryIsMatch, thisRef.current?.parentNode as HTMLElement, 10);
    if (!visibleLinkCategory && refLinkCategoryIsMatch?.current)
      refLinkCategoryIsMatch.current.scrollIntoView();
  }, [props.selectedCategory]);

  useMutationObservable(thisRef.current, scrollInToView, config);

  useLayoutEffect(() => {
    scrollInToView();
  }, [scrollInToView]);


  const classes = useStyles();

  return (
    <div className={classes.menuContainer}>
      {props.categories.map((category) => {
        const categoryIsMatch = category === props.selectedCategory;
        return (
          <Accordion
            style={{margin: 0}}
            onChange={() => changeExpand(category)}
            ref={categoryIsMatch ? thisRef : undefined}
            defaultExpanded={categoryIsMatch}
            key={category.label}
            expanded={expanded.includes(category)}
          >
            <AccordionSummary
              expandIcon={<ExpandMore/>}
              className={classes.accordionSummary}
              disabled={!category.enabled && !category.entries.some(s => s.enabled)}
            >
              {category.icon}
              <Typography style={{marginLeft: theme.spacing(1.2)}}
              >
                {category.label}

              </Typography>
            </AccordionSummary>
            <AccordionDetails className={props.classes.navlist}>
              <List>
                {category.entries.map((entry, i) => (
                  <NavLink
                    to={entry.path}
                    key={entry.path}
                    ref={props.path.includes(entry.path) ? refLinkCategoryIsMatch : undefined}
                    onClick={evt => handleClick(evt, entry)}
                    className={(isActive) => {
                      let classname = entry.enabled ? '' : classes.disabled;
                      if(isActive.isActive)
                      {
                        classname = clsx(classes.active, classname);
                      }
                      return classname;
                    }}
                  >
                    <ListItem button>
                      <ListItemText sx={{pl: 4}}>{entry.label}</ListItemText>
                    </ListItem>
                  </NavLink>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
});
